import type { SerializedStyles } from '@emotion/react';
import { Box, Button, Flex } from '@inflearn/ds-react-next';
import { isNil } from 'lodash-es';
import { useEffect, useState } from 'react';
import { Controller, type SubmitHandler, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../../resources/ApiError';
import { logger } from '../../../resources/Logger';
import { type Captcha, useCaptcha } from '../../hooks/useCaptcha';
import useClientAuthPhoneVerificationCreateMutation from '../../mutations/useClientAuthPhoneVerificationCreateMutation';
import useClientAuthPhoneVerificationPublicCreateMutation from '../../mutations/useClientAuthPhoneVerificationPublicCreateMutation';
import { ERROR_MESSAGE } from '../../validators/constants';
import { PhoneInput } from './PhoneInput';
import ValidationMessage from './ValidationMessage';

interface PhoneVerificationFormType {
  phone: string;
  countryCode: string;
}

interface PhoneVerificationFormProps {
  onSend?: () => void;
  isSend: boolean;
  showPhoneInputLabel?: boolean;
  styleSubmitButton?: SerializedStyles;
  autoFocus?: boolean;
  existingPhoneNumber?: string;
  withCaptcha?: boolean;
  /**
   * useCaptcha는 전역 onload 콜백과 인자 없는 turnstile.reset()을 쓰므로 한 페이지에 인스턴스가 둘이면 서로 덮어쓴다.
   * 페이지가 다른 요청에도 캡차를 쓸 때는 페이지의 인스턴스를 넘겨 공유한다.
   */
  sharedCaptcha?: Captcha;
  /**
   * 인증이 끝난 번호를 바꾸지 못하게 입력을 잠그고 발송 버튼을 숨긴다.
   */
  isVerified?: boolean;
  /**
   * 지정하지 않으면 캡차를 쓰는 화면에서만 꽉 찬 너비가 된다.
   */
  fullWidthSendButton?: boolean;
}

export function PhoneVerificationForm({
  onSend,
  isSend,
  showPhoneInputLabel,
  styleSubmitButton,
  autoFocus = false,
  existingPhoneNumber,
  withCaptcha = false,
  sharedCaptcha,
  isVerified = false,
  fullWidthSendButton = withCaptcha,
}: PhoneVerificationFormProps) {
  const formContext = useFormContext<PhoneVerificationFormType>();
  const phone = useWatch({ control: formContext.control, name: 'phone' });

  const [disabled, setDisabled] = useState(false);
  const [hasExceededMaxAttempts, setHasExceededMaxAttempts] = useState(false);

  const shouldRenderOwnCaptcha = withCaptcha && isNil(sharedCaptcha);
  const ownCaptcha = useCaptcha({ enabled: shouldRenderOwnCaptcha });
  const captcha = sharedCaptcha ?? ownCaptcha;
  const { t } = useTranslation();

  const handleError = (error: unknown) => {
    if (!ApiError.isApiError(error)) {
      logger.error(error);
      formContext.setError('phone', {
        type: 'manual',
        message: hasExceededMaxAttempts
          ? ERROR_MESSAGE.OVER_MAX_COUNT()
          : t('잠시 후 다시 시도해 주세요.'),
      });
      return;
    }

    const message = (() => {
      if (ApiError.isBadRequest(error.statusCode)) {
        return ERROR_MESSAGE.IS_NOT_KOREAN_NUMBER();
      }

      if (ApiError.isTooManyRequest(error.statusCode)) {
        setHasExceededMaxAttempts(true);
        return ERROR_MESSAGE.OVER_MAX_COUNT();
      }

      if (error.errorCode === 'INVALID_CAPTCHA_TOKEN') {
        return ERROR_MESSAGE.INVALID_CAPTCHA_TOKEN();
      }

      return ERROR_MESSAGE.IS_NOT_EMPTY();
    })();

    formContext.setError('phone', {
      type: 'manual',
      message,
    });
  };

  const mutationWithCaptcha = useClientAuthPhoneVerificationPublicCreateMutation({
    onSuccess: () => {
      captcha.resetCaptchaToken();
      onSend?.();
    },
    onError: (error) => {
      captcha.resetCaptchaToken();
      handleError(error);
    },
  });

  const mutationWithoutCaptcha = useClientAuthPhoneVerificationCreateMutation({
    onSuccess: onSend,
    onError: handleError,
  });

  const mutation = withCaptcha ? mutationWithCaptcha : mutationWithoutCaptcha;
  // 캡차 토큰을 기다리는 동안도 발송 중으로 본다.
  const isSending = mutation.isLoading || (withCaptcha && captcha.isPending);

  const handleSendRequest: SubmitHandler<PhoneVerificationFormType> = ({ countryCode, phone }) => {
    if (withCaptcha) {
      captcha.runWhenCaptchaExist((captchaToken) => {
        mutationWithCaptcha.mutate({
          countryCode,
          phone,
          captchaToken,
        });
      });
    } else {
      mutationWithoutCaptcha.mutate({
        countryCode,
        phone,
      });
    }
  };

  useEffect(() => {
    if (isNil(existingPhoneNumber)) {
      return;
    }

    setDisabled(existingPhoneNumber === phone);
  }, [phone]);

  return (
    <Flex justify="center" w="100%">
      {shouldRenderOwnCaptcha && <captcha.DevTools />}
      <Flex
        component="form"
        direction="column"
        w="100%"
        onSubmit={formContext.handleSubmit(handleSendRequest)}
      >
        <Controller
          control={formContext.control}
          name="phone"
          render={({ field: phoneField, fieldState }) => (
            <Controller
              control={formContext.control}
              name="countryCode"
              render={({ field: countryCodeField }) => (
                <PhoneInput
                  code={countryCodeField.value}
                  phoneNumber={phoneField.value}
                  onCodeChange={(value) => {
                    countryCodeField.onChange(value);
                    formContext.trigger('phone');
                  }}
                  onNumberChange={phoneField.onChange}
                  error={
                    fieldState.error?.message && (
                      <ValidationMessage message={fieldState.error.message} />
                    )
                  }
                  showLabel={showPhoneInputLabel}
                  autoFocus={autoFocus}
                  disabled={isVerified}
                >
                  {!isVerified && (
                    <Button
                      variant="default"
                      size="md"
                      styles={{
                        root: styleSubmitButton,
                      }}
                      miw={127}
                      fullWidth={fullWidthSendButton}
                      type="submit"
                      disabled={disabled}
                      loading={isSending}
                    >
                      {isSend ? t('재전송') : t('인증 요청')}
                    </Button>
                  )}
                </PhoneInput>
              )}
            />
          )}
        />
        {shouldRenderOwnCaptcha && (
          <Box ref={captcha.captchaRef} className={captcha.captchaClassName} mx="auto" />
        )}
      </Flex>
    </Flex>
  );
}
